import React, { forwardRef } from 'react';

export type ButtonVariant = 'primary' | 'warn' | 'ghost' | 'soft' | 'bad' | 'ok' | 'flat';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      leftIcon,
      rightIcon,
      loading = false,
      disabled = false,
      fullWidth = false,
      className = '',
      style,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Base Styles
    const baseStyle: React.CSSProperties = {
      display: fullWidth ? 'flex' : 'inline-flex',
      width: fullWidth ? '100%' : 'auto',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'var(--font-sans)',
      fontWeight: 500,
      borderRadius: 'var(--z-radius-md, 8px)',
      border: 'none',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      opacity: isDisabled ? 0.6 : 1,
      transition: 'all var(--z-ease-fast, 150ms)',
      userSelect: 'none',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      lineHeight: 1.25,
      boxSizing: 'border-box'
    };

    // Size mappings
    const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
      sm: {
        padding: '5px 10px',
        fontSize: '12px',
        height: '28px',
        gap: '6px'
      },
      md: {
        padding: '7px 14px',
        fontSize: '13.5px',
        height: '36px',
        gap: '8px'
      },
      lg: {
        padding: '10px 18px',
        fontSize: '15px',
        height: '42px',
        gap: '10px'
      }
    };

    // Variant mappings
    const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
      primary: {
        backgroundColor: 'var(--z-accent)',
        color: 'var(--z-on-accent)',
        boxShadow: 'var(--z-sh-4)'
      },
      warn: {
        backgroundColor: 'var(--z-warn)',
        color: '#ffffff',
        boxShadow: 'var(--z-sh-4)'
      },
      bad: {
        backgroundColor: 'var(--z-bad)',
        color: '#ffffff',
        boxShadow: 'var(--z-sh-4)'
      },
      ok: {
        backgroundColor: 'var(--z-ok)',
        color: '#ffffff',
        boxShadow: 'var(--z-sh-4)'
      },
      ghost: {
        backgroundColor: 'transparent',
        color: 'var(--z-text)'
      },
      soft: {
        backgroundColor: 'var(--z-accent-soft)',
        color: 'var(--z-accent-copy)'
      },
      flat: {
        backgroundColor: 'var(--z-fill)',
        color: 'var(--z-text)'
      }
    };

    const mergedStyle: React.CSSProperties = {
      ...baseStyle,
      ...sizeStyles[size],
      ...variantStyles[variant],
      ...style
    };

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading ? 'true' : undefined}
        style={mergedStyle}
        className={`z-btn z-btn-${variant} z-btn-${size} ${className}`}
        {...props}
      >
        {loading ? (
          <span
            className="animate-zspin"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1em',
              height: '1em',
              marginRight: children ? '4px' : '0'
            }}
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              style={{ width: '100%', height: '100%' }}
            >
              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
            </svg>
          </span>
        ) : (
          leftIcon && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )
        )}
        {children && <span>{children}</span>}
        {!loading && rightIcon && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            aria-hidden="true"
          >
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
